/*
  Copyright (C) 2024 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
*/
import { defineStore } from "pinia";
import { useConfigStore } from "./config";
import collabService from "@/services/CollabService";

export const useCollabStore = defineStore({
  id: "CollabStore",

  state: () => ({
    // Feature availability
    enabled: false,
    loaded: false,

    // Active session
    activeSession: null,
    activeNodeId: null,

    // Connected peers
    connectedPeers: [],

    // Connection state
    connectionState: "disconnected",

    // User's assigned color
    myColor: null,

    // Error state
    lastError: null,

    // Configuration
    maxPeers: 5,
  }),

  getters: {
    /**
     * Returns the number of connected peers (excluding self)
     */
    peerCount: (state) => state.connectedPeers.length,

    /**
     * Returns true if there are other peers connected
     */
    hasActivePeers: (state) => state.connectedPeers.length > 0,

    /**
     * Returns true if collaborative editing is currently active
     */
    isCollabActive: (state) => state.activeSession !== null,

    /**
     * Returns true if connected to the collaboration server
     */
    isConnected: (state) => state.connectionState === "connected",

    /**
     * Returns true if currently reconnecting
     */
    isReconnecting: (state) => state.connectionState === "reconnecting",

    /**
     * Returns true if collab feature is available for use
     */
    isAvailable: (state) => state.enabled && state.loaded,
  },

  actions: {
    /**
     * Initializes the collaborative editing store.
     * Checks if the feature is enabled in configuration.
     */
    async init() {
      try {
        const config = useConfigStore();

        // Check if collab is enabled in config
        this.enabled = config.COLLAB_ENABLED !== false;
        this.maxPeers = config.COLLAB_MAX_PEERS || 5;

        this.loaded = true;
        console.log("[CollabStore] Initialized, enabled:", this.enabled);
      } catch (error) {
        console.error("[CollabStore] Failed to initialize:", error);
        this.enabled = false;
        this.loaded = true;
      }
    },

    /**
     * Checks if collaborative editing is available for a specific node.
     *
     * @param {string} nodeId - The node ID to check
     * @returns {Promise<Object>} Availability info
     */
    async checkAvailability(nodeId) {
      if (!this.enabled) {
        return { available: false, reason: "disabled" };
      }

      try {
        return await collabService.checkAvailability(nodeId);
      } catch (error) {
        console.error("[CollabStore] Availability check failed:", error);
        return { available: false, reason: "error", error };
      }
    },

    /**
     * Updates the active session information
     *
     * @param {Object} session - Session info from server
     */
    setActiveSession(session) {
      this.activeSession = session?.sessionId || null;
      this.activeNodeId = session?.nodeId || null;
      this.myColor = session?.color || null;
    },

    /**
     * Updates the list of connected peers
     *
     * @param {Array} peers - List of peer objects
     */
    updatePeers(peers) {
      this.connectedPeers = peers || [];
    },

    /**
     * Updates the connection state
     *
     * @param {string} state - Connection state
     */
    setConnectionState(state) {
      this.connectionState = state;
    },

    /**
     * Sets the last error
     *
     * @param {Error} error - Error object
     */
    setError(error) {
      this.lastError = error;
    },

    /**
     * Clears the current session
     */
    clearSession() {
      this.activeSession = null;
      this.activeNodeId = null;
      this.connectedPeers = [];
      this.myColor = null;
      this.connectionState = "disconnected";
      this.lastError = null;
    },

    /**
     * Adds a peer to the connected peers list
     *
     * @param {Object} peer - Peer object
     */
    addPeer(peer) {
      const index = this.connectedPeers.findIndex(
        (p) => p.clientId === peer.clientId,
      );
      if (index === -1) {
        this.connectedPeers.push(peer);
      }
    },

    /**
     * Removes a peer from the connected peers list
     *
     * @param {string} clientId - Client ID to remove
     */
    removePeer(clientId) {
      const index = this.connectedPeers.findIndex(
        (p) => p.clientId === clientId,
      );
      if (index !== -1) {
        this.connectedPeers.splice(index, 1);
      }
    },

    /**
     * Updates a peer's cursor position
     *
     * @param {string} clientId - Client ID
     * @param {number} position - Cursor position
     * @param {number} anchor - Selection anchor
     */
    updatePeerCursor(clientId, position, anchor) {
      const peer = this.connectedPeers.find((p) => p.clientId === clientId);
      if (peer) {
        peer.cursorPosition = position;
        peer.cursorAnchor = anchor;
      }
    },
  },
});
